import { useSyncExternalStore } from "react";
import { EXAM_BY_ID } from "../data/exams";
import { ALL_QUESTIONS, QUESTION_BY_ID, questionsForExam } from "../data/questions";
import type { ExamId, Question } from "../data/types";
import { hashString, mulberry32, newSeed, shuffle } from "./random";
import { isCorrect, isPass, scaledScore } from "./scoring";
import type { Attempt, DomainTally, Progress, SessionKind } from "./progress";
import { isDue } from "./srs";
import { pick, type L, type Lang } from "../i18n/lang";

export interface Session {
  id: string;
  kind: SessionKind;
  examId?: ExamId;
  title: string;
  /** Tiêu đề song ngữ (phiên mới); `title` giữ bản tiếng Việt cho dữ liệu cũ. */
  label?: L;
  questionIds: string[];
  /** Thứ tự hiển thị phương án: optionOrder[qid][vị trí hiển thị] = chỉ số gốc. */
  optionOrder: Record<string, number[]>;
  /** Lựa chọn theo chỉ số GỐC. */
  answers: Record<string, number[]>;
  /** Practice/daily/review: câu đã nộp & xem giải thích. */
  revealed: Record<string, boolean>;
  flagged: string[];
  index: number;
  startedAt: number;
  /** Chỉ có ở mock: thời lượng cho phép. */
  durationMs?: number;
}

/** Thứ tự phương án xáo trộn tất định theo id câu — dùng nơi không có phiên (bài học, xem lại kết quả). */
export function stableOptionOrder(q: Question): number[] {
  return shuffle(q.options.map((_, i) => i), mulberry32(hashString(`opt:${q.id}`)));
}

export const DAILY_COUNT = 5;
export const REVIEW_MAX = 20;

/** Thời lượng thi thử theo cùng nhịp đề thật (durationMin / items mỗi câu). */
export function mockDurationMs(examId: ExamId, questionCount: number): number {
  const e = EXAM_BY_ID[examId];
  return Math.round((questionCount * e.durationMin * 60_000) / e.items);
}

/** Tiêu đề hiển thị theo ngôn ngữ — dùng cho cả Session và Attempt. */
export function displayTitle(x: { title: string; label?: L }, lang: Lang): string {
  return x.label ? pick(x.label, lang) : x.title;
}

/** Chế độ có phản hồi tức thì sau từng câu. */
export function isInstantFeedback(kind: SessionKind): boolean {
  return kind !== "mock" && kind !== "diagnostic";
}

function makeSession(
  kind: SessionKind,
  label: L,
  questions: Question[],
  seed: number,
  extra: Partial<Session> = {},
): Session {
  const rand = mulberry32(seed ^ 0x9e3779b9);
  const optionOrder: Record<string, number[]> = {};
  for (const q of questions) optionOrder[q.id] = shuffle(q.options.map((_, i) => i), rand);
  return {
    id: `${kind}-${Date.now().toString(36)}-${seed.toString(36)}`,
    kind,
    title: label.vi,
    label,
    questionIds: questions.map((q) => q.id),
    optionOrder,
    answers: {},
    revealed: {},
    flagged: [],
    index: 0,
    startedAt: Date.now(),
    ...extra,
  };
}

export function buildFixedSession(kind: SessionKind, label: L, questions: Question[], seed = newSeed()): Session {
  return makeSession(kind, label, questions, seed);
}

export type PracticeFilter = "all" | "unseen" | "wrong" | "bookmarked";

export interface PracticeOptions {
  domains: string[];
  count: number;
  filter: PracticeFilter;
}

export function selectPracticeQuestions(
  examId: ExamId,
  opts: PracticeOptions,
  p: Progress,
  seed: number,
): Question[] {
  const domainSet = new Set(opts.domains);
  const pool = questionsForExam(examId).filter((q) => {
    if (domainSet.size && !domainSet.has(q.domain)) return false;
    const s = p.questions[q.id];
    switch (opts.filter) {
      case "unseen": return !s;
      case "wrong": return !!s && !s.lastCorrect;
      case "bookmarked": return p.bookmarks.includes(q.id);
      default: return true;
    }
  });
  return shuffle(pool, mulberry32(seed)).slice(0, Math.max(1, opts.count));
}

export function buildPractice(examId: ExamId, opts: PracticeOptions, p: Progress, seed = newSeed()): Session | null {
  const qs = selectPracticeQuestions(examId, opts, p, seed);
  if (!qs.length) return null;
  const code = EXAM_BY_ID[examId].code;
  return makeSession("practice", { vi: `Luyện tập ${code}`, en: `Practice ${code}` }, qs, seed, { examId });
}

/**
 * Chọn câu cho bài thi thử, tối đa bằng số câu đề thật.
 * Đề có kịch bản: rút N kịch bản, giữ câu theo nhóm kịch bản.
 * Đề không có kịch bản: chia suất theo tỷ trọng domain chính thức (largest remainder).
 */
export function selectMockQuestions(examId: ExamId, seed: number): Question[] {
  const exam = EXAM_BY_ID[examId];
  const rand = mulberry32(seed);
  const pool = questionsForExam(examId);
  if (exam.scenarios?.length && exam.scenariosPerMock) {
    const picked = shuffle(exam.scenarios, rand).slice(0, exam.scenariosPerMock);
    return picked.flatMap((sc) => shuffle(pool.filter((q) => q.scenario === sc.id), rand)).slice(0, exam.items);
  }
  const target = Math.min(exam.items, pool.length);
  const byDomain = new Map(exam.domains.map((d) => [d.id, shuffle(pool.filter((q) => q.domain === d.id), rand)]));
  const quota = new Map<string, number>();
  const rema: { id: string; r: number }[] = [];
  for (const d of exam.domains) {
    const exact = (target * d.weight) / 100;
    const n = Math.min(Math.floor(exact), byDomain.get(d.id)?.length ?? 0);
    quota.set(d.id, n);
    rema.push({ id: d.id, r: exact - Math.floor(exact) });
  }
  let left = target - [...quota.values()].reduce((a, b) => a + b, 0);
  // Phát phần dư: ưu tiên phần lẻ lớn; domain nào hết câu thì nhường cho domain khác.
  rema.sort((a, b) => b.r - a.r);
  while (left > 0) {
    let gave = false;
    for (const { id } of rema) {
      if (left <= 0) break;
      const have = byDomain.get(id)?.length ?? 0;
      const q = quota.get(id) ?? 0;
      if (q < have) { quota.set(id, q + 1); left--; gave = true; }
    }
    if (!gave) break;
  }
  const chosen = exam.domains.flatMap((d) => (byDomain.get(d.id) ?? []).slice(0, quota.get(d.id) ?? 0));
  return shuffle(chosen, rand);
}

export function buildMock(examId: ExamId, seed = newSeed()): Session {
  const qs = selectMockQuestions(examId, seed);
  const code = EXAM_BY_ID[examId].code;
  return makeSession("mock", { vi: `Thi thử ${code}`, en: `Mock exam ${code}` }, qs, seed, {
    examId,
    durationMs: mockDurationMs(examId, qs.length),
  });
}

/** Thử thách ngày: cùng một bộ câu cho mọi người trong cùng ngày (seed theo ngày). */
export function selectDailyQuestions(day: string): Question[] {
  return shuffle(ALL_QUESTIONS, mulberry32(hashString(`daily:${day}`))).slice(0, DAILY_COUNT);
}

export function buildDaily(day: string): Session {
  const d = day.split("-").reverse().join("/");
  return makeSession("daily", { vi: `Thử thách ngày ${d}`, en: `Daily challenge ${d}` }, selectDailyQuestions(day), hashString(day));
}

export function dueQuestionIds(p: Progress, now: number): string[] {
  return Object.entries(p.questions)
    .filter(([id, s]) => QUESTION_BY_ID.has(id) && isDue(s.srs, now))
    .sort((a, b) => a[1].srs.due - b[1].srs.due)
    .map(([id]) => id);
}

export const REVIEW_LABEL: L = { vi: "Ôn tập ngắt quãng", en: "Spaced review" };

export function buildReview(ids: string[], label: L = REVIEW_LABEL, seed = newSeed()): Session | null {
  const qs = ids.map((id) => QUESTION_BY_ID.get(id)).filter((q): q is Question => !!q).slice(0, REVIEW_MAX);
  if (!qs.length) return null;
  return makeSession("review", label, qs, seed);
}

/** Khóa domain trong Attempt.byDomain — gồm cả mã đề vì id domain có thể trùng giữa các đề. */
export function domainKey(exam: ExamId, domain: string): string {
  return `${exam}:${domain}`;
}

/** Tên domain chính thức (tiếng Anh, giống nhau ở cả hai ngôn ngữ). */
export function domainLabel(key: string): string {
  const [exam, domain] = key.split(":");
  const e = exam && exam in EXAM_BY_ID ? EXAM_BY_ID[exam as ExamId] : undefined;
  return e?.domains.find((d) => d.id === domain)?.name ?? domain ?? key;
}

export function gradeSession(s: Session, finishedAt: number, timedOut = false): Attempt {
  let correct = 0;
  const byDomain: Record<string, DomainTally> = {};
  for (const id of s.questionIds) {
    const q = QUESTION_BY_ID.get(id);
    if (!q) continue;
    const ok = isCorrect(s.answers[id] ?? [], q.answer);
    if (ok) correct++;
    const t = (byDomain[domainKey(q.exam, q.domain)] ??= { correct: 0, total: 0 });
    t.total++;
    if (ok) t.correct++;
  }
  const total = s.questionIds.length;
  const scaled = scaledScore(correct, total);
  return {
    id: s.id,
    kind: s.kind,
    ...(s.examId ? { examId: s.examId } : {}),
    title: s.title,
    ...(s.label ? { label: s.label } : {}),
    startedAt: s.startedAt,
    finishedAt,
    questionIds: s.questionIds,
    answers: s.answers,
    correct,
    total,
    scaled,
    passed: isPass(scaled),
    byDomain,
    ...(timedOut ? { timedOut: true } : {}),
  };
}

// ── Lưu phiên đang làm (khôi phục khi tải lại trang) ─────────────
export const SESSION_KEY = "claude-cert-prep:session:v1";
let current: Session | null = readSession();
const listeners = new Set<() => void>();

function readSession(): Session | null {
  if (typeof localStorage === "undefined") return null;
  try {
    const raw = localStorage.getItem(SESSION_KEY);
    if (!raw) return null;
    const s = JSON.parse(raw) as Session;
    // Bỏ phiên hỏng hoặc chứa câu đã bị gỡ khỏi ngân hàng.
    if (!Array.isArray(s.questionIds) || !s.questionIds.every((id) => QUESTION_BY_ID.has(id))) return null;
    return s;
  } catch {
    return null;
  }
}

export const sessionStore = {
  get: (): Session | null => current,
  subscribe(l: () => void): () => void {
    listeners.add(l);
    return () => listeners.delete(l);
  },
  set(s: Session | null): void {
    current = s;
    try {
      if (s) localStorage.setItem(SESSION_KEY, JSON.stringify(s));
      else localStorage.removeItem(SESSION_KEY);
    } catch {
      /* bỏ qua */
    }
    for (const l of listeners) l();
  },
  update(fn: (s: Session) => Session): void {
    if (current) sessionStore.set(fn(current));
  },
  reload(): void {
    current = readSession();
    for (const l of listeners) l();
  },
};

export function useSession(): Session | null {
  return useSyncExternalStore(sessionStore.subscribe, sessionStore.get, sessionStore.get);
}
